// @ts-nocheck
import { api } from '../api/client';
import { getCurrentUser, refreshSession } from '../auth/session';
import { setBrowserCookie } from '../utils/cookies';
import { showToast } from '../components/toast/toast';
import { t } from '../i18n';
import { translateApiMessage } from '../i18n/api-errors';
import { getMetaCookies, trackMetaEvent } from '../utils/meta-events';
import { META_CHECKOUT_CURRENCY, META_CHECKOUT_VALUE } from '../utils/meta-checkout-value';
import { openPaywallModal } from '../components/modals/paywall';

export function isPethernityActive(user = getCurrentUser()): boolean {
  return !!user?.pethernity?.active;
}

let publicAdsenseClientId: string | null = null;

export function setPublicAdsenseClientId(id: string | null): void {
  publicAdsenseClientId = typeof id === 'string' && id.trim() ? id.trim() : null;
}

export function shouldShowAds(user = getCurrentUser()): boolean {
  if (!user) return true;
  if (user.ads && typeof user.ads.enabled === 'boolean') return user.ads.enabled;
  return !isPethernityActive(user);
}

export function adsenseClientId(user = getCurrentUser()): string | null {
  const id = user?.ads?.clientId;
  if (typeof id === 'string' && id.trim()) return id.trim();
  return publicAdsenseClientId;
}

export function isPethernityRequiredError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const row = err as { code?: string; message?: string };
  return (
    row.code === 'PETHERNITY_REQUIRED' ||
    row.message === 'Pethernity membership required'
  );
}

export function openPaywall(reason?: string): void {
  openPaywallModal(reason);
}

export function handleEntitlementError(err: unknown, fallback?: string): boolean {
  if (!isPethernityRequiredError(err)) return false;
  openPaywall(fallback || t('pethernity.required'));
  return true;
}

export async function startPethernityCheckout(): Promise<void> {
  const user = await refreshSession();
  if (!user) {
    window.openAuthModal('login', t('pethernity.loginToUpgrade'), 'purchase');
    return;
  }
  if (!user.emailVerified) {
    showToast(t('auth.verifyBeforeUpgrade'), 'info', 7000);
    return;
  }
  if (isPethernityActive(user)) {
    showToast(t('pethernity.alreadyActive'), 'info', 5000);
    return;
  }

  try {
    const metaCookies = getMetaCookies();
    const { data, error } = await api.POST('/payments/checkout', {
      body: {
        analytics: {
          ...metaCookies,
          eventSourceUrl: window.location.href,
        },
      },
    });

    if (error || !data) {
      const rawMessage = (error as { message?: string })?.message;
      const msg =
        rawMessage === 'Email verification required'
          ? t('auth.verifyBeforeUpgrade')
          : translateApiMessage(rawMessage) ?? t('grave.unexpectedError');
      if (isPethernityRequiredError(error)) {
        openPaywall();
        return;
      }
      showToast(`❌ ${msg}`);
      return;
    }

    setBrowserCookie('pendingPethernityId', data.pendingId);
    try {
      sessionStorage.setItem('pendingPethernityCheckout', '1');
    } catch {
      /* ignore */
    }

    try {
      trackMetaEvent(
        'InitiateCheckout',
        {
          content_type: 'product',
          content_name: 'Pethernity yearly',
          currency: META_CHECKOUT_CURRENCY,
          value: META_CHECKOUT_VALUE,
        },
        data.pendingId,
      );
    } catch (metaErr) {
      console.error('Meta checkout tracking failed', metaErr);
    }

    window.location.href = data.checkoutUrl;
  } catch (err) {
    console.error('Pethernity checkout failed', err);
    showToast(t('grave.checkoutError', { message: err?.message ?? err }));
  }
}
